import React from "react";
import { TrustRolesMapProps } from "../../../../types/cdd-records.types";
import { TrustPersonCard as PersonCard } from "./TrustPersonCard";

export const TrustRolesMap: React.FC<TrustRolesMapProps> = ({
  roles,
  onPersonClick,
}) => {
  if (!roles) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No trust roles defined
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Trust role information is not available.
        </p>
      </div>
    );
  }

  const { trustees, beneficiaries, settlors, appointers } = roles;

  return (
    <div className="space-y-6">
      {/* Visual Trust Structure Diagram */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Trust Structure Overview
        </h3>

        <div className="relative">
          {/* Simple visual representation */}
          <div className="flex flex-col items-center gap-4">
            {/* Settlors */}
            {settlors.length > 0 && (
              <div className="text-center">
                <div className="text-xs text-purple-600 font-semibold uppercase mb-2">
                  Settlors
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {settlors.map((s) => (
                    <span
                      key={s.id}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      {s.firstName} {s.lastName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Arrow down */}
            <svg
              className="w-6 h-6 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>

            {/* Trust Box */}
            <div className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white text-center shadow-lg">
              <div className="text-lg font-bold">TRUST</div>
              <div className="text-xs opacity-80">Managed by Trustees</div>
            </div>

            {/* Arrows */}
            <div className="flex items-center gap-8">
              <svg
                className="w-6 h-6 text-gray-300 transform rotate-45"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
              <svg
                className="w-6 h-6 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
              <svg
                className="w-6 h-6 text-gray-300 transform -rotate-45"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>

            {/* Bottom row - Trustees and Beneficiaries */}
            <div className="flex flex-wrap justify-center gap-8">
              {/* Trustees */}
              <div className="text-center">
                <div className="text-xs text-blue-600 font-semibold uppercase mb-2">
                  Trustees ({trustees.length})
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {trustees.slice(0, 3).map((t) => (
                    <span
                      key={t.id}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {t.firstName} {t.lastName}
                    </span>
                  ))}
                  {trustees.length > 3 && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                      +{trustees.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Beneficiaries */}
              <div className="text-center">
                <div className="text-xs text-green-600 font-semibold uppercase mb-2">
                  Beneficiaries ({beneficiaries.length})
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {beneficiaries.slice(0, 3).map((b) => (
                    <span
                      key={b.id}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                    >
                      {b.firstName} {b.lastName}
                    </span>
                  ))}
                  {beneficiaries.length > 3 && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                      +{beneficiaries.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Role Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trustees */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-blue-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Trustees
              </h4>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded">
                {trustees.length}
              </span>
            </div>
            <p className="text-xs text-blue-700 mt-1">
              Responsible for managing the trust assets and making distributions
            </p>
          </div>
          <div className="p-4 space-y-3">
            {trustees.map((trustee) => (
              <PersonCard
                key={trustee.id}
                person={trustee}
                roleType="trustee"
                onClick={onPersonClick}
              />
            ))}
            {trustees.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No trustees on record
              </p>
            )}
          </div>
        </div>

        {/* Beneficiaries */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-green-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Beneficiaries
              </h4>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-medium rounded">
                {beneficiaries.length}
              </span>
            </div>
            <p className="text-xs text-green-700 mt-1">
              Persons or entities entitled to benefit from the trust
            </p>
          </div>
          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {beneficiaries.map((beneficiary) => (
              <PersonCard
                key={beneficiary.id}
                person={beneficiary}
                roleType="beneficiary"
                onClick={onPersonClick}
              />
            ))}
            {beneficiaries.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No beneficiaries on record
              </p>
            )}
          </div>
        </div>

        {/* Settlors */}
        {settlors.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-semibold text-purple-900 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Settlors
                </h4>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded">
                  {settlors.length}
                </span>
              </div>
              <p className="text-xs text-purple-700 mt-1">
                Person(s) who established the trust and provided initial assets
              </p>
            </div>
            <div className="p-4 space-y-3">
              {settlors.map((settlor) => (
                <PersonCard
                  key={settlor.id}
                  person={settlor}
                  roleType="settlor"
                  onClick={onPersonClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Appointers */}
        {appointers && appointers.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-orange-50">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-semibold text-orange-900 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                  Appointers
                </h4>
                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded">
                  {appointers.length}
                </span>
              </div>
              <p className="text-xs text-orange-700 mt-1">
                Person(s) with power to appoint or remove trustees
              </p>
            </div>
            <div className="p-4 space-y-3">
              {appointers.map((appointer) => (
                <PersonCard
                  key={appointer.id}
                  person={appointer}
                  roleType="appointer"
                  onClick={onPersonClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustRolesMap;
